// src/components/ToolsModal.js
import React, { useState, useRef } from 'react';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';

const ToolsModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [specificDays, setSpecificDays] = useState(false);
  const [startDate, setStartDate] = useState('2025-09-01');
  const [endDate, setEndDate] = useState('2025-09-01');
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);
  const templateWorkbookRef = useRef(null);

  React.useEffect(() => {
    if (isOpen) {
      loadTemplate();
    }
  }, [isOpen]);

  const loadTemplate = async () => {
    try {
      const response = await fetch('/template.xlsx');
      if (!response.ok) {
        throw new Error('Шаблон не найден. Убедитесь, что файл template.xlsx находится в папке public.');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      templateWorkbookRef.current = workbook;
      console.log('Шаблон загружен автоматически');
    } catch (error) {
      console.error('Ошибка загрузки шаблона:', error);
      showError('Не удалось загрузить шаблон. Убедитесь, что файл template.xlsx находится в папке public.');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      processSelectedFile(file);
    }
  };

  const processSelectedFile = (file) => {
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      showError('Пожалуйста, выберите файл Excel (.xlsx или .xls)');
      return;
    }

    setFileInfo({
      name: file.name,
      size: formatFileSize(file.size)
    });
    
    hideError();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => hideError(), 5000);
  };

  const hideError = () => {
    setError('');
  };

  const showProgress = () => {
    setLoading(true);
    setProgress(0);
    setProgressText('Подготовка...');
  };

  const hideProgress = () => {
    setLoading(false);
    setProgress(0);
    setProgressText('');
  };

  const updateProgress = async (percent, text) => {
    return new Promise(resolve => {
      setProgress(Math.max(0, Math.min(100, percent)));
      setProgressText(text || '');
      setTimeout(resolve, 0);
    });
  };

  // Функция для отладки структуры Excel файла
  const debugExcelStructure = async (file) => {
    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);
          
          console.log('=== ОТЛАДКА СТРУКТУРЫ EXCEL ===');
          console.log('Количество листов:', workbook.worksheets.length);
          
          workbook.worksheets.forEach((sheet, index) => {
            console.log(`Лист ${index + 1}:`, sheet.name);
            
            // Покажем первые несколько строк для понимания структуры
            let rowCount = 0;
            sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
              if (rowNumber <= 15 && rowCount < 15) {
                const values = row.values;
                console.log(`Строка ${rowNumber}:`, values);
                rowCount++;
              }
            });
          });
          console.log('=== КОНЕЦ ОТЛАДКИ ===');
        } catch (error) {
          console.error('Ошибка отладки:', error);
        }
        resolve();
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const generateMenus = async () => {
    if (!fileInputRef.current?.files[0]) {
      showError('Пожалуйста, выберите файл');
      return;
    }

    if (!templateWorkbookRef.current) {
      showError('Шаблон не загружен. Подождите немного и попробуйте снова.');
      return;
    }

    if (specificDays) {
      if (!startDate || !endDate) {
        showError('Пожалуйста, выберите начальную и конечную даты');
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        showError('Начальная дата не может быть больше конечной');
        return;
      }
    }

    try {
      showProgress();
      await updateProgress(5, 'Начало обработки...');
      
      // Добавляем отладку структуры файла
      await debugExcelStructure(fileInputRef.current.files[0]);
      
      await updateProgress(10, 'Чтение файла меню...');
      const menuData = await parseMenuData(fileInputRef.current.files[0]);
      
      await updateProgress(30, 'Подготовка данных...');
      await new Promise(resolve => setTimeout(resolve, 50));

      await updateProgress(40, 'Генерация ежедневных меню...');
      const zipBlob = await createMenuArchive(menuData);
      
      await updateProgress(95, 'Создание архива...');
      await new Promise(resolve => setTimeout(resolve, 100));

      await updateProgress(100, 'Готово!');
      await new Promise(resolve => setTimeout(resolve, 300));

      showDownloadLink(zipBlob);

    } catch (error) {
      console.error('Ошибка генерации:', error);
      showError('Ошибка при обработке файла: ' + error.message);
      hideProgress();
    }
  };

  const parseMenuData = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);
          
          // Получаем первый лист
          const worksheet = workbook.worksheets[0];
          
          // Проверяем существование листа
          if (!worksheet) {
            if (workbook.worksheets.length === 0) {
              throw new Error('В загруженном файле нет ни одного листа. Пожалуйста, проверьте файл.');
            }
            throw new Error('Не удалось получить доступ к первому листу Excel файла.');
          }
          
          console.log('Работаем с листом:', worksheet.name);
          
          const days = [];
          let currentWeek = null;
          let currentDayOfWeek = null;
          let currentMeal = null;
          let lastDayNumber = 0;
          let dataFound = false;

          // Получаем название школы из ячейки B2 с проверкой
          let schoolName = '';
          try {
            const schoolNameCell = worksheet.getCell('B2');
            schoolName = schoolNameCell && schoolNameCell.value ? schoolNameCell.value : 'Укажите наименование ОУ';
          } catch (cellError) {
            console.warn('Не удалось прочитать ячейку B2:', cellError);
            schoolName = 'Укажите наименование ОУ';
          }
          
          console.log('School name from B2:', schoolName);

          // Перебираем строки
          let rowCount = 0;
          worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            rowCount++;
            // Пропускаем заголовки
            if (rowNumber <= 5) return;

            const values = row.values;
            if (!values || values.length < 5) return;
            
            // Определяем неделю (колонка A - индекс 1)
            if (values[1] !== undefined && values[1] !== null && values[1] !== '') {
              const weekValue = values[1];
              if (typeof weekValue === 'number' && weekValue > 0) {
                currentWeek = weekValue;
              } else if (typeof weekValue === 'string' && !isNaN(parseInt(weekValue))) {
                currentWeek = parseInt(weekValue);
              }
            }

            // Определяем день недели (колонка B - индекс 2)
            if (values[2] !== undefined && values[2] !== null && values[2] !== '') {
              const dayValue = values[2];
              let dayNum = null;
              
              if (typeof dayValue === 'number' && dayValue > 0) {
                dayNum = dayValue;
              } else if (typeof dayValue === 'string' && !isNaN(parseInt(dayValue))) {
                dayNum = parseInt(dayValue);
              }
              
              if (dayNum !== null) {
                currentDayOfWeek = dayNum;
                currentMeal = null;
                
                if (currentWeek === 1) {
                  lastDayNumber = currentDayOfWeek;
                } else if (currentWeek) {
                  lastDayNumber = (currentWeek - 1) * 5 + currentDayOfWeek;
                }
              }
            }

            // Определяем прием пищи (колонка C - индекс 3)
            if (values[3] && values[3] !== '') {
              const mealValue = values[3].toString().trim().toLowerCase();
              if (mealValue.includes('завтрак')) {
                currentMeal = 'Завтрак';
              } else if (mealValue.includes('обед')) {
                currentMeal = 'Обед';
              }
            }

            // Парсим блюдо
            if (currentWeek && currentDayOfWeek && currentMeal && values[5] && values[5] !== '') {
              const dishName = values[5]?.toString().trim();
              
              // Пропускаем итоги
              if (dishName && !dishName.toLowerCase().includes('итого')) {
                
                dataFound = true;
                const absoluteDayNumber = lastDayNumber || 1;
                const dayKey = `week${currentWeek}_day${currentDayOfWeek}_abs${absoluteDayNumber}`;
                let day = days.find(d => d.key === dayKey);
                
                if (!day) {
                  day = {
                    key: dayKey,
                    week: currentWeek,
                    dayOfWeek: currentDayOfWeek,
                    absoluteDay: absoluteDayNumber,
                    meals: {
                      'Завтрак': [],
                      'Обед': []
                    }
                  };
                  days.push(day);
                }

                const mealData = {
                  section: values[4] ? values[4].toString().trim().toLowerCase() : '',
                  dish: dishName,
                  weight: parseNumber(values[6]),
                  proteins: parseNumber(values[7]),
                  fats: parseNumber(values[8]),
                  carbs: parseNumber(values[9]),
                  calories: parseNumber(values[10]),
                  recipe: values[11] ? values[11].toString().trim() : ''
                };

                if (mealData.dish && mealData.dish !== '') {
                  day.meals[currentMeal].push(mealData);
                }
              }
            }
          });

          console.log(`Всего обработано строк: ${rowCount}`);
          
          if (!dataFound) {
            throw new Error('Не удалось найти данные о блюдах в файле. Проверьте, что файл содержит столбцы: Неделя (A), День недели (B), Прием пищи (C), Раздел меню (D), Блюда (E)');
          }
          
          if (days.length === 0) {
            throw new Error('Не удалось сформировать дни из загруженного файла. Проверьте структуру данных.');
          }

          // Сортируем дни по absoluteDay
          days.sort((a, b) => a.absoluteDay - b.absoluteDay);
          
          console.log(`Всего дней найдено: ${days.length}`);
          days.forEach(day => {
            console.log(`День ${day.absoluteDay}: Завтрак - ${day.meals['Завтрак'].length} блюд, Обед - ${day.meals['Обед'].length} блюд`);
          });

          resolve({ 
            days,
            schoolName: schoolName || 'ГБОУ гимназия №399' 
          });
        } catch (error) {
          console.error('Ошибка парсинга Excel:', error);
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Ошибка чтения файла'));
      reader.readAsArrayBuffer(file);
    });
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'string') {
      if (value.startsWith('=')) return 0;
      const cleanValue = value.toString().replace(/,/g, '.').replace(/\s/g, '');
      const num = parseFloat(cleanValue);
      return isNaN(num) ? 0 : num;
    }
    return typeof value === 'number' ? value : 0;
  };

  const generateWorkingDays = (startDateStr, endDateStr) => {
    const workingDays = [];
    const currentDate = new Date(startDateStr);
    const end = new Date(endDateStr);

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const createMenuArchive = async (menuData) => {
    const zip = new JSZip();

    if (specificDays) {
      const workingDays = generateWorkingDays(startDate, endDate);
      const sortedDays = menuData.days;

      if (workingDays.length === 0) {
        throw new Error('В выбранном диапазоне дат нет рабочих дней (понедельник-пятница)');
      }

      for (let i = 0; i < workingDays.length; i++) {
        const progress = 45 + (45 * i / workingDays.length);
        const currentDate = workingDays[i];
        const formattedDate = formatDate(currentDate);
        const displayDate = formatDateForDisplay(currentDate);
        
        await updateProgress(Math.floor(progress), `Генерация меню на ${displayDate} (${i + 1}/${workingDays.length})`);
        await new Promise(resolve => setTimeout(resolve, 20));

        const dayIndex = i % sortedDays.length;
        const dayData = sortedDays[dayIndex];
        
        const fullDayData = {
          ...dayData,
          schoolName: menuData.schoolName
        };
        
        const excelBuffer = await createDayWorkbook(fullDayData, currentDate);
        const fileName = `${formattedDate}-sm.xlsx`;
        zip.file(fileName, excelBuffer);
      }
    } else {
      const sortedDays = menuData.days;

      for (let i = 0; i < sortedDays.length; i++) {
        const progress = 45 + (45 * i / sortedDays.length);
        
        await updateProgress(Math.floor(progress), `Генерация меню для дня ${sortedDays[i].absoluteDay} (${i + 1}/${sortedDays.length})`);
        await new Promise(resolve => setTimeout(resolve, 20));
        
        const fullDayData = {
          ...sortedDays[i],
          schoolName: menuData.schoolName
        };
        
        const excelBuffer = await createDayWorkbook(fullDayData);
        const fileName = `День ${sortedDays[i].absoluteDay}.xlsx`;
        zip.file(fileName, excelBuffer);
      }
    }

    return await zip.generateAsync({ type: 'blob' });
  };

  const createDayWorkbook = async (dayData, specificDate = null) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const templateBuffer = await templateWorkbookRef.current.xlsx.writeBuffer();
      await workbook.xlsx.load(templateBuffer);
      
      const worksheet = workbook.getWorksheet(1);
      
      // Проверяем существование worksheet
      if (!worksheet) {
        throw new Error('Не удалось получить рабочий лист из шаблона');
      }

      fillWorksheetData(worksheet, dayData);

      if (specificDate) {
        try {
          const dateCell = worksheet.getCell('J1');
          if (dateCell) {
            dateCell.value = formatDateForDisplay(specificDate);
          }
        } catch (cellError) {
          console.warn('Не удалось заполнить ячейку J1:', cellError);
        }
      }

      return await workbook.xlsx.writeBuffer();
    } catch (error) {
      console.error('Ошибка в createDayWorkbook:', error);
      throw error;
    }
  };

  const fillWorksheetData = (worksheet, dayData) => {
    try {
      // Проверяем существование worksheet
      if (!worksheet) {
        console.error('Worksheet не найден');
        return;
      }

      // Заполняем название школы в ячейку B1
      if (dayData.schoolName) {
        try {
          const b1Cell = worksheet.getCell('B1');
          if (b1Cell) {
            b1Cell.value = dayData.schoolName;
          }
        } catch (cellError) {
          console.warn('Не удалось заполнить ячейку B1:', cellError);
        }
      }

      // Определяем диапазоны строк для завтрака и обеда
      const breakfastRows = [4, 5, 6, 7, 8, 9, 10, 11];
      const lunchRows = [14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];

      if (dayData.meals && dayData.meals['Завтрак'] && dayData.meals['Завтрак'].length > 0) {
        fillMealSection(worksheet, dayData.meals['Завтрак'], breakfastRows, 'Завтрак');
      }
      
      if (dayData.meals && dayData.meals['Обед'] && dayData.meals['Обед'].length > 0) {
        fillMealSection(worksheet, dayData.meals['Обед'], lunchRows, 'Обед');
      }
    } catch (error) {
      console.error('Ошибка в fillWorksheetData:', error);
      throw new Error('Ошибка при заполнении данных в шаблоне');
    }
  };

  const fillMealSection = (worksheet, meals, availableRows, mealType) => {
    try {
      // Проверяем наличие templateWorksheet
      if (!templateWorkbookRef.current) {
        console.error('templateWorkbookRef.current не определен');
        return;
      }

      const templateWorksheet = templateWorkbookRef.current.getWorksheet(1);
      
      if (!templateWorksheet) {
        console.error('templateWorksheet не найден');
        return;
      }

      // Сначала очищаем все строки в диапазоне
      availableRows.forEach(rowNum => {
        clearMealRow(worksheet, templateWorksheet, rowNum);
      });

      // Затем заполняем все блюда по порядку
      meals.forEach((meal, index) => {
        if (index < availableRows.length) {
          const rowNum = availableRows[index];
          
          try {
            updateCell(worksheet, templateWorksheet, 'B' + rowNum, meal.section || '');
            updateCell(worksheet, templateWorksheet, 'C' + rowNum, meal.recipe || '');
            updateCell(worksheet, templateWorksheet, 'D' + rowNum, meal.dish || '');
            updateCell(worksheet, templateWorksheet, 'E' + rowNum, meal.weight || 0);
            updateCell(worksheet, templateWorksheet, 'G' + rowNum, meal.calories || 0);
            updateCell(worksheet, templateWorksheet, 'H' + rowNum, meal.proteins || 0);
            updateCell(worksheet, templateWorksheet, 'I' + rowNum, meal.fats || 0);
            updateCell(worksheet, templateWorksheet, 'J' + rowNum, meal.carbs || 0);
          } catch (cellError) {
            console.warn(`Ошибка при заполнении строки ${rowNum}:`, cellError);
          }
        } else {
          console.warn(`Не хватает строк для ${mealType}: блюдо "${meal.dish}" не поместилось`);
        }
      });
    } catch (error) {
      console.error('Ошибка в fillMealSection:', error);
    }
  };

  const clearMealRow = (worksheet, templateWorksheet, rowNum) => {
    try {
      if (!worksheet || !templateWorksheet) return;
      
      const cellsToClear = ['B', 'C', 'D', 'E', 'G', 'H', 'I', 'J'];
      cellsToClear.forEach(col => {
        try {
          updateCell(worksheet, templateWorksheet, col + rowNum, '');
        } catch (cellError) {
          // Игнорируем ошибки для отдельных ячеек
        }
      });
    } catch (error) {
      console.warn(`Ошибка при очистке строки ${rowNum}:`, error);
    }
  };

  const updateCell = (worksheet, templateWorksheet, address, value) => {
    try {
      if (!worksheet) {
        console.warn(`worksheet не определен при обновлении ${address}`);
        return;
      }

      const cell = worksheet.getCell(address);
      
      if (!cell) {
        console.warn(`Ячейка ${address} не найдена`);
        return;
      }
      
      cell.value = value;
      
      // Копируем стили из шаблона
      if (templateWorksheet) {
        try {
          const templateCell = templateWorksheet.getCell(address);
          
          if (templateCell) {
            if (templateCell.fill) {
              cell.fill = JSON.parse(JSON.stringify(templateCell.fill));
            }
            if (templateCell.border) {
              cell.border = JSON.parse(JSON.stringify(templateCell.border));
            }
            if (templateCell.font) {
              cell.font = JSON.parse(JSON.stringify(templateCell.font));
            }
            if (templateCell.alignment) {
              cell.alignment = JSON.parse(JSON.stringify(templateCell.alignment));
            }
            if (templateCell.numFmt) {
              cell.numFmt = templateCell.numFmt;
            }
          }
        } catch (styleError) {
          // Игнорируем ошибки копирования стилей
        }
      }
    } catch (error) {
      console.warn(`Ошибка при обновлении ячейки ${address}:`, error);
    }
  };

  const showDownloadLink = (blob) => {
    const url = URL.createObjectURL(blob);
    const fileName = `Ежедневные_меню_${new Date().toISOString().split('T')[0]}.zip`;
    
    setDownloadUrl(url);
    setFileName(fileName);
    hideProgress();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processSelectedFile(file);
      fileInputRef.current.files = e.dataTransfer.files;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content compact" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🍽️ Генератор меню</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div 
            className="upload-section compact" 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="upload-icon">📊</div>
            <h4>Загрузите файл типового меню</h4>
            <p>Excel (.xlsx, .xls)</p>
            
            <input 
              type="file" 
              ref={fileInputRef}
              className="file-input" 
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button 
              className="upload-btn compact" 
              onClick={() => fileInputRef.current?.click()}
            >
              Выбрать файл
            </button>

            {fileInfo && (
              <div className="file-info compact show">
                <strong>Файл:</strong> <span>{fileInfo.name}</span>
                <strong>Размер:</strong> <span>{fileInfo.size}</span>
              </div>
            )}
          </div>

          <div className="options-section compact">
            <div className="option-row">
              <input 
                type="checkbox" 
                id="specificDaysCheckbox"
                checked={specificDays}
                onChange={(e) => setSpecificDays(e.target.checked)}
              />
              <label htmlFor="specificDaysCheckbox">Для конкретных дней</label>
            </div>
            
            <div className={`date-range compact ${specificDays ? 'show' : ''}`}>
              <div className="date-inputs compact">
                <div className="date-input">
                  <label>Начальная дата:</label>
                  <input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (!endDate) {
                        setEndDate(e.target.value);
                      }
                    }}
                    min="2025-09-01"
                  />
                </div>
                <div className="date-input">
                  <label>Конечная дата:</label>
                  <input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </div>
              </div>
              <p className="date-note">
                Субботы и воскресенья не включаются
              </p>
            </div>
          </div>

          {error && (
            <div className="error-message compact show">
              {error}
            </div>
          )}

          {loading && (
            <div className="menu-progress-section compact show">
              <h4>Генерация меню...</h4>
              <div className="menu-progress-bar">
                <div 
                  className="menu-progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="menu-progress-text">{progressText} ({progress}%)</p>
            </div>
          )}

          {downloadUrl && (
            <div className="download-section compact show">
              <h4>✅ Меню готово!</h4>
              <a 
                href={downloadUrl} 
                className="download-btn compact" 
                download={fileName}
                onClick={onClose}
              >
                📥 Скачать архив
              </a>
            </div>
          )}
        </div>

        <div className="modal-footer compact">
          <button className="btn secondary compact" onClick={onClose}>
            Закрыть
          </button>
          <button 
            className="btn primary compact" 
            onClick={generateMenus}
            disabled={loading || !fileInfo}
          >
            {loading ? (
              <>
                <span className="menu-loading-spinner"></span>
                Генерация...
              </>
            ) : 'Сгенерировать меню'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolsModal;